import React, { useState } from 'react';
import { PageHeader } from '../../../shared/components';
import { PageLayout } from '../../../shared/layout/page/PageLayout';
import { IVehiclePageData } from '../../domain/pages/IVehiclePageData';
import { observer } from 'mobx-react';
import { useUrlForPageId } from '../../../app/pages/ui/utils/useUrlForPageId';
import { PassengersPageId } from '../../../app/pages/domain/constants/PageId';
import { VehiclesList } from './VehiclesList';
import { VehicleDialog } from './VehicleDialog';
import { VehiclesCardContainer } from './VehiclesCardContainer';
import { useVehicleStores } from '../domain/stores/useVehicleStores';
import { useTranslation } from '../../../shared/i18n/translate/useTranslation';
import { VehiclesEmptyStateContainer } from './VehiclesEmptyStateContainer';
import { useLoadMyVehicles } from '../domain/use-cases/useLoadMyVehicles';
import { IVehicle } from '../../../shared/api/gateway/models/Vehicle';
import { VehiclesShimmer } from './VehiclesShimmer';
import ContentWrap from '../../../../ui/components/Layout/ContentWrap';
import { SuccessBar } from '../../../shared/components/success/SuccessBar';
import { CancelIcon } from '../../../../ui';
import { Color } from '../../../shared/styles/Color';
import styled from '@emotion/styled';
import { DeleteVehicleDialog } from './DeleteVehicleDialog';
import { trackVehicleDialog } from '../shared/analytics';
import { EVENT_LABEL_VEHICLES_ACCOUNT } from '../../../shared/analytics';
import { trackRemoveVehicle } from '../shared/analytics/trackRemoveVehicle';
import { useRedirectIfNotAuthenticated } from '../../../auth/shared/store/useRedirectIfNotAuthenticated';

interface IVehiclePageProps extends IVehiclePageData {}

export const VehiclesPage = observer(({ page }: IVehiclePageProps) => {
	const { t } = useTranslation();

	const redirectUrl = useUrlForPageId(PassengersPageId.Home);
	useRedirectIfNotAuthenticated(redirectUrl);

	const { getVehicleStore, deleteVehicleStore } = useVehicleStores();
	const {
		value,
		load: loadVehicles,
		isLoading: isLoadingVehicles,
	} = getVehicleStore;

	const { load: deleteVehicle } = deleteVehicleStore;

	const vehicles = value || [];
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [successMessage, setSuccessMessage] = useState<string | undefined>();
	const [deleteVehicleError, setDeleteVehicleError] = useState<
		Error | undefined
	>(undefined);

	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [currentVehicle, setCurrentVehicle] = useState<IVehicle>();

	const openFormDialog = (vehicle?: IVehicle) => {
		setCurrentVehicle(vehicle ?? undefined);
		setIsDialogOpen(true);

		const mode = vehicle ? 'edit' : 'add';
		trackVehicleDialog(mode);
	};

	const closeFormDialog = () => {
		setIsDialogOpen(false);
		setCurrentVehicle(undefined);
	};

	const onDelete = async () => {
		try {
			setIsSubmitting(true);

			await deleteVehicle(currentVehicle?.vehicleId as string);
			trackRemoveVehicle();

			loadVehicles();

			handleSuccess(t('account_vehicles_delete_success_label'));

			closeDeleteDialog();
		} catch (error) {
			setDeleteVehicleError(error as Error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const openDeleteDialog = () => {
		const deleteCurrentVehicle = currentVehicle;
		closeFormDialog();
		setCurrentVehicle(deleteCurrentVehicle);
		setIsDeleteDialogOpen(true);
	};

	const closeDeleteDialog = () => {
		setIsDeleteDialogOpen(false);

		setCurrentVehicle(undefined);

		setIsSubmitting(false);
	};

	const [showSuccessBar, setShowSuccessBar] = useState(false);

	const hideSuccessBar = () => {
		setShowSuccessBar(false);
	};

	const handleSuccess = (msg: string) => {
		setSuccessMessage(msg);
		setShowSuccessBar(true);
	};

	useLoadMyVehicles();

	const editVehicle = (vehicle: IVehicle) => {
		openFormDialog(vehicle);
	};

	return (
		<PageLayout page={page}>
			<PageHeader page={page} />
			<ContentWrap>
				{isLoadingVehicles ? (
					<VehiclesShimmer size="large" />
				) : vehicles?.length === 0 ? (
					<VehiclesEmptyStateContainer
						onAddVehicle={() => openFormDialog()}
						addVehicleButtonText={t(
							'account_vehicles_add_new_vehicle_button_label',
						)}
					/>
				) : (
					<VehiclesCardContainer
						onRetry={loadVehicles}
						vehiclesList={vehicles}
						onAddVehicle={() => openFormDialog()}
						addVehicleButtonText={t(
							'account_vehicles_add_new_vehicle_button_label',
						)}
						listComponent={
							<VehiclesList
								vehiclesList={vehicles}
								onAddVehicle={() => openFormDialog()}
								editVehicle={editVehicle}
								addVehicleButtonText={t(
									'account_vehicles_add_new_vehicle_button_label',
								)}
							/>
						}
					/>
				)}

				{showSuccessBar && (
					<StyledSuccessBar onClick={hideSuccessBar}>
						<span>{successMessage}</span>
						<CancelIcon />
					</StyledSuccessBar>
				)}
			</ContentWrap>

			{isDialogOpen && (
				<VehicleDialog
					isOpen={isDialogOpen}
					closeDialog={closeFormDialog}
					currentVehicle={currentVehicle}
					onDelete={openDeleteDialog}
					handleSuccess={() =>
						handleSuccess(t('account_vehicles_save_success_label'))
					}
					analyticsScreenParamValue={EVENT_LABEL_VEHICLES_ACCOUNT}
				/>
			)}

			<DeleteVehicleDialog
				isOpen={isDeleteDialogOpen}
				closeDialog={closeDeleteDialog}
				onDelete={onDelete}
				isDeleting={isSubmitting}
				error={deleteVehicleError}
			/>
		</PageLayout>
	);
});

const StyledSuccessBar = styled(SuccessBar)`
	display: flex;
	align-items: center;
	justify-content: space-between;

	span {
		width: 90%;
		text-align: left;
		line-height: normal;
	}

	${CancelIcon} {
		cursor: pointer;

		g {
			stroke: ${Color.TextWhite};
		}
	}
`;
